import DeleteIcon from '@mui/icons-material/Delete';
import { IconButton, SelectChangeEvent, Stack, TextField, Tooltip, Typography } from '@mui/material';

import {
    TemplateComponentButtonType,
    WhatsAppButton,
    WhatsAppCopyOfferCodeButton,
    WhatsAppFlowButton,
    WhatsAppPhoneButton,
    WhatsAppQuickReplyButton,
    WhatsAppUrlButton,
} from 'domains/content/types';
import Select from 'domains/core/components/Select';

const BUTTON_TYPE_OPTIONS = [
    { value: TemplateComponentButtonType.QUICK_REPLY, label: 'Quick Reply' },
    { value: TemplateComponentButtonType.URL, label: 'URL' },
    { value: TemplateComponentButtonType.PHONE_NUMBER, label: 'Phone Number' },
    { value: TemplateComponentButtonType.COPY_CODE, label: 'OTP (Copy Code)' },
    { value: TemplateComponentButtonType.COPY_OFFER_CODE, label: 'Copy Offer Code' },
    { value: TemplateComponentButtonType.FLOW, label: 'Flow' },
];

const BUTTON_TEXT_MAX = 25;

export type Props = {
    button: WhatsAppButton;
    index: number;
    error?: string;
    onChange: (index: number, button: WhatsAppButton) => void;
    onRemove: (index: number) => void;
};

const ButtonItem = ({ button, index, error, onChange, onRemove }: Props) => {
    const handleTypeChange = (e: SelectChangeEvent<unknown>) => {
        const newType = e.target.value as TemplateComponentButtonType;
        // Reset to a minimal valid button of the new type
        const defaults: Record<TemplateComponentButtonType, WhatsAppButton> = {
            [TemplateComponentButtonType.QUICK_REPLY]: { type: TemplateComponentButtonType.QUICK_REPLY, text: '' },
            [TemplateComponentButtonType.URL]: { type: TemplateComponentButtonType.URL, text: '', url: '' },
            [TemplateComponentButtonType.PHONE_NUMBER]: {
                type: TemplateComponentButtonType.PHONE_NUMBER,
                text: '',
                phone_number: '',
            },
            [TemplateComponentButtonType.COPY_CODE]: { type: TemplateComponentButtonType.COPY_CODE },
            [TemplateComponentButtonType.COPY_OFFER_CODE]: {
                type: TemplateComponentButtonType.COPY_OFFER_CODE,
                copy_code: '',
            },
            [TemplateComponentButtonType.FLOW]: { type: TemplateComponentButtonType.FLOW, text: '', flow_id: '' },
        };
        onChange(index, defaults[newType]);
    };

    const updateText = (text: string) => onChange(index, { ...button, text } as WhatsAppButton);

    return (
        <Stack
            spacing={1.5}
            sx={{ border: '1px solid', borderColor: error ? 'error.main' : 'divider', borderRadius: 1, p: 2 }}
            data-testid={`button-item-${index}`}
        >
            <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                    Button {index + 1}
                </Typography>
                <Tooltip title="Remove button">
                    <IconButton
                        size="small"
                        aria-label="Remove button"
                        data-testid={`remove-button-${index}`}
                        onClick={() => onRemove(index)}
                    >
                        <DeleteIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Stack>

            <Select
                data-testid={`button-type-select-${index}`}
                label="Button type"
                value={button.type}
                onChange={handleTypeChange}
                size="small"
                values={BUTTON_TYPE_OPTIONS}
                inputProps={{ 'aria-label': `Button ${index + 1} type` }}
            />

            {/* QUICK_REPLY */}
            {button.type === TemplateComponentButtonType.QUICK_REPLY && (
                <TextField
                    data-testid={`button-text-${index}`}
                    label="Button text"
                    value={(button as WhatsAppQuickReplyButton).text}
                    onChange={(e) => updateText(e.target.value.slice(0, BUTTON_TEXT_MAX))}
                    helperText={`${(button as WhatsAppQuickReplyButton).text.length} / ${BUTTON_TEXT_MAX}`}
                    size="small"
                    fullWidth
                    inputProps={{ maxLength: BUTTON_TEXT_MAX, 'aria-label': 'Button text' }}
                />
            )}

            {/* URL */}
            {button.type === TemplateComponentButtonType.URL && (
                <Stack spacing={1}>
                    <TextField
                        data-testid={`button-text-${index}`}
                        label="Button text"
                        value={(button as WhatsAppUrlButton).text}
                        onChange={(e) => updateText(e.target.value.slice(0, BUTTON_TEXT_MAX))}
                        helperText={`${(button as WhatsAppUrlButton).text.length} / ${BUTTON_TEXT_MAX}`}
                        size="small"
                        fullWidth
                        inputProps={{ maxLength: BUTTON_TEXT_MAX, 'aria-label': 'Button text' }}
                    />
                    <TextField
                        data-testid={`button-url-${index}`}
                        label="URL"
                        value={(button as WhatsAppUrlButton).url}
                        onChange={(e) => onChange(index, { ...button, url: e.target.value } as WhatsAppUrlButton)}
                        helperText={`${
                            (button as WhatsAppUrlButton).url.length
                        } / 2000 · Use {{1}} for a dynamic path segment`}
                        size="small"
                        fullWidth
                        inputProps={{ maxLength: 2000, 'aria-label': 'Button URL' }}
                    />
                </Stack>
            )}

            {/* PHONE_NUMBER */}
            {button.type === TemplateComponentButtonType.PHONE_NUMBER && (
                <Stack spacing={1}>
                    <TextField
                        data-testid={`button-text-${index}`}
                        label="Button text"
                        value={(button as WhatsAppPhoneButton).text}
                        onChange={(e) => updateText(e.target.value.slice(0, BUTTON_TEXT_MAX))}
                        helperText={`${(button as WhatsAppPhoneButton).text.length} / ${BUTTON_TEXT_MAX}`}
                        size="small"
                        fullWidth
                        inputProps={{ maxLength: BUTTON_TEXT_MAX, 'aria-label': 'Button text' }}
                    />
                    <TextField
                        data-testid={`button-phone-${index}`}
                        label="Phone number"
                        value={(button as WhatsAppPhoneButton).phone_number}
                        onChange={(e) =>
                            onChange(index, { ...button, phone_number: e.target.value } as WhatsAppPhoneButton)
                        }
                        helperText="Include country code, e.g. +1 555 123 4567"
                        size="small"
                        fullWidth
                        inputProps={{ 'aria-label': 'Phone number' }}
                    />
                </Stack>
            )}

            {/* COPY_CODE */}
            {button.type === TemplateComponentButtonType.COPY_CODE && (
                <Typography variant="body2" color="text.secondary">
                    Displays a fixed "Copy Code" button. Use in authentication templates only.
                </Typography>
            )}

            {/* COPY_OFFER_CODE */}
            {button.type === TemplateComponentButtonType.COPY_OFFER_CODE && (
                <TextField
                    data-testid={`button-offer-code-${index}`}
                    label="Offer code"
                    value={(button as WhatsAppCopyOfferCodeButton).copy_code}
                    onChange={(e) =>
                        onChange(index, { ...button, copy_code: e.target.value } as WhatsAppCopyOfferCodeButton)
                    }
                    helperText="4–15 characters"
                    size="small"
                    fullWidth
                    inputProps={{ minLength: 4, maxLength: 15, 'aria-label': 'Offer code' }}
                />
            )}

            {/* FLOW */}
            {button.type === TemplateComponentButtonType.FLOW && (
                <Stack spacing={1}>
                    <TextField
                        data-testid={`button-text-${index}`}
                        label="Button text"
                        value={(button as WhatsAppFlowButton).text}
                        onChange={(e) => updateText(e.target.value.slice(0, BUTTON_TEXT_MAX))}
                        helperText={`${(button as WhatsAppFlowButton).text.length} / ${BUTTON_TEXT_MAX}`}
                        size="small"
                        fullWidth
                        inputProps={{ maxLength: BUTTON_TEXT_MAX, 'aria-label': 'Button text' }}
                    />
                    <TextField
                        data-testid={`button-flow-id-${index}`}
                        label="Flow ID"
                        value={(button as WhatsAppFlowButton).flow_id}
                        onChange={(e) => onChange(index, { ...button, flow_id: e.target.value } as WhatsAppFlowButton)}
                        helperText="Published Meta Flow ID"
                        size="small"
                        fullWidth
                        inputProps={{ 'aria-label': 'Flow ID' }}
                    />
                </Stack>
            )}

            {error && (
                <Typography variant="caption" color="error" data-testid={`button-error-${index}`}>
                    {error}
                </Typography>
            )}
        </Stack>
    );
};

export default ButtonItem;
